import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  HStack,
  Text,
  useBreakpointValue
} from "@chakra-ui/react"
import { getAvatarFallbackBG } from "@pathwright/ui/src/components/avatar/Avatar"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import TruncatedText from "@pathwright/ui/src/components/ui/TruncatedText"
import {
  hasEditorLevelAccess,
  hasModeratorLevelAccess
} from "@pathwright/web/src/modules/group/permissions"
import usePathAnalytics from "@pathwright/web/src/modules/path/usePathAnalytics"
import { usePathwrightContext } from "@pathwright/web/src/modules/pathwright/PathwrightContext"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import { getManageResourceUrl } from "@pathwright/web/src/modules/utils/urls"
import gql from "graphql-tag"
import { forwardRef } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import { OrderByDir } from "../api/generated"
import { COHORT_STATUS_COMPLETED } from "../group/constants"
import Share from "../share/Share"
import { useCohortMembers } from "./CohortMembers"
import CohortShareIslandBackgroundTaskContextProvider from "./CohortShareIslandBackgroundTaskContextProvider"
import { CohortControls } from "./controls/CohortControls"
import CohortSwitcher from "./switcher/CohortSwitcher"

const COHORT_QUERY = gql`
  query GroupQuery($id: Int!) {
    cohort: group(id: $id) {
      id
      status
      is_source_cohort
      resource {
        id
        slug
      }
      registration {
        id
        role
        send_due_notifications
        send_discussion_notifications
        path {
          id
        }
      }
      user_source_registration {
        id
        role
      }
      path(type: source) {
        id
      }
    }
  }
`

const GhostButton = forwardRef((props, ref) => (
  <Button
    ref={ref}
    variant={"ghost"}
    color="white"
    colorScheme={"whiteAlpha"}
    size="sm"
    fontSize="1em"
    p={2}
    cursor="pointer"
    {...props}
  />
))

const CohortControlsContainer = ({ cohort, section }) => {
  const analytics = usePathAnalytics(cohort)

  return (
    <CohortControls cohort={cohort} analytics={analytics} section={section}>
      {() => (
        <GhostButton>
          <Pathicon icon="ellipses-horizontal" />
        </GhostButton>
      )}
    </CohortControls>
  )
}

const CohortSwitcherTrigger = ({ title, icon, onClick, cohort }) => {
  const { userData } = useCohortMembers({
    cohortId: cohort.id,
    orderBy: { last_activity_time: OrderByDir.Desc }
  })
  const people = [...userData.staff, ...userData.students]

  const maxW = useBreakpointValue({
    base: "100px",
    sm: "200px"
  })

  const getName = (person) =>
    [person.first_name, person.last_name].filter(Boolean).join(" ")

  const getImage = (person) => {
    const { image } = person.profile
    return image && image.indexOf("profile-default") === -1 ? image : null
  }

  let inactiveProps = {}
  if (!onClick) {
    inactiveProps = {
      as: "div",
      bg: "transparent !important",
      cursor: "default !important"
    }
  }

  return (
    <TruncatedText tooltip={title}>
      <GhostButton p={1} onClick={onClick} {...inactiveProps}>
        <HStack spacing={1} minW={0}>
          {people.length ? (
            <AvatarGroup size="2xs" spacing={-2}>
              {[...userData.staff, ...userData.students]
                .slice(0, 3)
                .map((person) => (
                  <Avatar
                    key={person.id}
                    name={getName(person)}
                    src={getImage(person)}
                    bg={
                      getImage(person)
                        ? "gray.900"
                        : getAvatarFallbackBG({ user: person })
                    }
                    color="white"
                    border="none"
                  />
                ))}
            </AvatarGroup>
          ) : (
            <Pathicon icon={icon} />
          )}
          <Text
            as="span"
            noOfLines={1}
            maxW={maxW}
            color="white"
            display="block"
          >
            {title}
          </Text>
          {!!onClick && <Pathicon icon="chevron-down" />}
        </HStack>
      </GhostButton>
    </TruncatedText>
  )
}

const CohortShareTrigger = forwardRef(
  ({ title, icon, onToggle, cohort }, ref) => {
    const pwContext = usePathwrightContext()
    const variant = hasModeratorLevelAccess(pwContext, cohort)
      ? "ghost"
      : "solid"

    const showTitle = useBreakpointValue({
      base: false,
      sm: true
    })

    return (
      <GhostButton
        ref={ref}
        onClick={onToggle}
        onMouseDown={(e) => {
          // Prevents showing cohort visibility popover when closing...
          e.preventDefault()
        }}
        variant={variant}
      >
        <HStack spacing={1}>
          <Pathicon icon={icon} />
          {showTitle && <span>{title}</span>}
        </HStack>
      </GhostButton>
    )
  }
)

const CohortIslandShareTrigger = forwardRef(
  ({ title, icon, onToggle }, ref) => (
    <Box
      position="relative"
      borderRadius={"50px"}
      overflow="hidden"
      display="inline-flex"
    >
      <Box position="absolute" inset="0" />
      <GhostButton ref={ref} onClick={onToggle} variant="secondaryGray">
        <HStack spacing={1} lineHeight={1}>
          <Pathicon icon={icon} />
          <span>{title}</span>
        </HStack>
      </GhostButton>
    </Box>
  )
)

export const CohortShareIsland = ({ resourceId, cohortId, hideTabs }) => (
  <CohortShareIslandBackgroundTaskContextProvider
    cohortId={cohortId}
    resourceId={resourceId}
  >
    <Share
      context={{ type: "cohort", typeId: cohortId }}
      hideTabs={hideTabs}
      openWithKeys
    >
      {({ renderTrigger }) => renderTrigger(CohortIslandShareTrigger)}
    </Share>
  </CohortShareIslandBackgroundTaskContextProvider>
)

const CohortIsland = ({ cohortId, section }) => {
  const pwContext = usePathwrightContext()
  const query = useQuery(COHORT_QUERY, {
    variables: { id: cohortId },
    skip: !cohortId
  })
  const cohort = query.data?.cohort

  return cohort ? (
    <Box
      bg="gray.900"
      borderRadius={"50px"}
      p={1}
      position="relative"
      zIndex={10}
    >
      <HStack className="chakra-ui-reset-css" display="inline-flex" spacing={1}>
        <CohortSwitcher
          cohortId={cohort.id}
          resourceId={cohort.resource.id}
          section={section}
        >
          {({ renderTrigger }) =>
            renderTrigger(CohortSwitcherTrigger, { cohort })
          }
        </CohortSwitcher>
        {!cohort.is_source_cohort &&
          cohort.status !== COHORT_STATUS_COMPLETED && (
            <CohortShareIslandBackgroundTaskContextProvider
              cohortId={cohortId}
              resourceId={cohort.resource.id}
            >
              <Share
                context={{ type: "cohort", typeId: cohortId }}
                openWithKeys
              >
                {({ renderTrigger }) =>
                  renderTrigger(CohortShareTrigger, { cohort })
                }
              </Share>
            </CohortShareIslandBackgroundTaskContextProvider>
          )}
        {cohort.is_source_cohort ? (
          hasEditorLevelAccess(pwContext, cohort) ? (
            <GhostButton
              to={getManageResourceUrl(cohort.resource.id)}
              as={ReactRouterLink}
            >
              <Pathicon icon="gear" />
            </GhostButton>
          ) : null
        ) : hasModeratorLevelAccess(pwContext, cohort) ? (
          <CohortControlsContainer cohort={cohort} section={section} />
        ) : null}
      </HStack>
    </Box>
  ) : null
}

export default CohortIsland
